import { useCallback, useContext, useEffect, useMemo, useRef, useState, useReducer, ChangeEvent } from 'react';
import { Button, Checkbox, Grid } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import type {
    CellEditingStoppedEvent,
    CellValueChangedEvent,
    FirstDataRenderedEvent,
} from 'ag-grid-community/dist/types/core/events';
import * as React from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import type { ProcessCellForExportParams } from 'ag-grid-community/dist/types/core/interfaces/exportParams';
import { DATE_FORMATS } from '@callmart/types';
import Container from '../components/Layout/Container';
import UserContext from '../store/User/UserContext';
import {
    useAlertMessage,
    useCookieLocation,
    useCustomStyles,
    useIsMounted,
    useLocationsByLoggedInUserRole,
} from '../hooks';
import ButtonIconRenderer from '../components/AgGrid/ButtonIconRenderer';
import { createTableRows } from '../utils/agGridUtils';
import { formatNumber, numberValueFormatter, parseStringToFloat, parseLocalizedNumber } from '../utils/numberUtils';
import { getUpcomingMonths, isStringInYearMonthFormat } from '../utils/dateUtils';
import { addRowsInMap } from '../components/StrategicVolume/helpers/utils';
import {
    createOneStrategicVolumeReport,
    createStrategicVolumeReport,
    markStrategicVolumeReportsRelease,
    uploadStrategicVolumes,
} from '../services/strategicVolumeReport';
import ConfirmationDialog from '../components/UI/Dialogs/ConfirmationDialog';
import { StrategicVolumeReportRelease } from '../enums/StrategicVolumeReportRelease';
import { updateStrategicVolume } from '../services/strategicVolume';
import MatchingCapacityReportTooltip from '../components/StrategicVolume/MatchingCapacityReportTooltip';
import { parseXLSXData } from '../utils/xlsxUtils';
import PercentageLoadingOverlay from '../components/Loaders/PercentageLoadingOverlay';
import UserRoleLocation from '../components/General/UserRoleLocation';
import UploadXlsxFileButton from '../components/Buttons/UploadXlsxFileButton';
import {
    parseUploadResponse,
    setCommonStateAfterDataUpload,
    showErrorNotification,
    showErrorUploadNotification,
    showSuccessfulUploadNotification,
} from '../utils/componentUtils';
import LastUpload from '../components/General/LastUpload';
import TableGridItem from '../components/UI/TableGridItem';
import GridContainer from '../components/Layout/GridContainer';
import {
    getStrategicVolumeReportsQueryKey,
    useCompareStrategicVolumeAndCapacityReport,
    useLastStrategicVolumeReportUploadDate,
    useStrategicVolumeReports,
} from '../hooks/api/strategicVolumeReports';
import { queryClient } from '../services/queryClient';
import { QueryKey } from '../enums/QueryKey';
import StrategicVolumeExportButton from '../components/StrategicVolume/StrategicVolumeExportButton';
import StrategicVolumeUploadResultsDialog from '../components/StrategicVolume/StrategicVolumeUploadResultsDialog';
import { StrategicVolumeReleaseReportsForm } from '../types/StrategicVolume';
import dayjs, { Dayjs } from '@utils/dayjs';

export const createMapWithTableRows = (
    monthColumns: Dayjs[],
    strategicVolumeReport: any,
    compareWithCapacityReport: boolean
) => {
    return addRowsInMap({ strategicVolumes: strategicVolumeReport, monthColumns, compareWithCapacityReport });
};

const isRowSelected = (row: { id: string }, selectedRows: any[]) => {
    return !!selectedRows.find((r) => parseInt(r.strategicVolumeId) === parseInt(row.id));
};

const getStrategicVolumeAndReportsFromRow = (row: {
    [x: string]: { release: any; strategicVolumeReportId: number };
    id?: any;
}): StrategicVolumeReleaseReportsForm => {
    const strategicVolumeReports = [];
    for (const key of Object.keys(row)) {
        if (isStringInYearMonthFormat(key)) {
            const strategicVolumeReportId = row[key].strategicVolumeReportId;

            if (strategicVolumeReportId)
                strategicVolumeReports.push({ id: strategicVolumeReportId, release: row[key].release });
        }
    }

    return { strategicVolumeId: row.id, strategicVolumeReports };
};

const StrategicVolume = () => {
    const classes = useCustomStyles();
    const { t, i18n } = useTranslation('bidding');

    const { dispatchAlert } = useAlertMessage();
    const loggedInUser = useContext(UserContext);
    const userId = useMemo(() => loggedInUser?.id, [loggedInUser]);
    // TEF aliases to WFM in HEMTService. Memo name kept for blast radius in this
    // page; the flag now means "WFM-or-equivalent manager view".
    const isTEFUser = useMemo(() => Boolean(loggedInUser?.isWFMUser), [loggedInUser]);
    const isOSPUser = useMemo(() => loggedInUser?.isOSPUser, [loggedInUser]);
    // WFM and WTM both inherit TEF's read view after the role split: same 18-month
    // window, navigation and data scope. isTEFUser still gates write capabilities
    // (edit/release/upload), which stay WFM-only — WTM is read-only.
    const managerView = useMemo(() => isTEFUser || Boolean(loggedInUser?.isWTMUser), [isTEFUser, loggedInUser]);
    const NUM_OF_MONTH_COLUMNS = useMemo(() => (managerView ? 18 : 7), [managerView]);

    const isForecasterFilterApplied = useRef(false);

    const isMounted = useIsMounted();
    const gridRef = useRef<AgGridReact>(null);
    const [locationOptions] = useLocationsByLoggedInUserRole();
    const [selectedLocation, setSelectedLocation] = useCookieLocation(locationOptions);
    const lastUploadDate = useLastStrategicVolumeReportUploadDate(selectedLocation);

    const [firstMonth, setFirstMonth] = useState(dayjs());
    const [showComparisonWithCapacityReport, setShowComparisonWithCapacityReport] = useState(false);
    const [selectedRows, setSelectedRows] = useState<StrategicVolumeReleaseReportsForm[]>([]);
    const selectedRowsRef = useRef(selectedRows);
    const freigabeHeaderForceUpdateRef = useRef<() => void>(() => {});

    const [loading, setLoading] = useState(false);
    const [percentage, setPercentage] = useState(0);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [uploadResults, setUploadResults] = useState<any[]>([]);
    const [showUploadResults, setShowUploadResults] = useState(false);

    const releaseType = useMemo(() => (managerView ? null : StrategicVolumeReportRelease.RELEASED), [managerView]);
    const monthColumns = useMemo(
        () => getUpcomingMonths(firstMonth, NUM_OF_MONTH_COLUMNS),
        [firstMonth, NUM_OF_MONTH_COLUMNS]
    );

    const strategicVolumeReportQueryKey = useMemo(
        () =>
            getStrategicVolumeReportsQueryKey({
                date: firstMonth,
                location: selectedLocation,
                user: userId,
                releaseType: managerView ? null : StrategicVolumeReportRelease.RELEASED,
                numOfMonthColumns: NUM_OF_MONTH_COLUMNS,
            }),
        [firstMonth, selectedLocation, userId, managerView, NUM_OF_MONTH_COLUMNS]
    );

    const { data: strategicVolumeReports } = useStrategicVolumeReports({
        date: firstMonth,
        location: selectedLocation,
        user: userId,
        releaseType,
        numOfMonthColumns: NUM_OF_MONTH_COLUMNS,
    });
    const { data: comparisonWithCapacityReport } = useCompareStrategicVolumeAndCapacityReport({
        date: firstMonth,
        location: selectedLocation,
        user: userId,
        releaseType,
        numOfMonthColumns: NUM_OF_MONTH_COLUMNS,
        showComparisonWithCapacityReport,
    });

    const tableRowsToShow = useMemo(
        () =>
            showComparisonWithCapacityReport && comparisonWithCapacityReport.length > 0
                ? comparisonWithCapacityReport
                : strategicVolumeReports,
        [showComparisonWithCapacityReport, comparisonWithCapacityReport, strategicVolumeReports]
    );
    const tableRows = useMemo(
        () =>
            tableRowsToShow.length > 0
                ? createTableRows(
                      createMapWithTableRows(monthColumns, tableRowsToShow, showComparisonWithCapacityReport),
                      'id'
                  )
                : [],
        [monthColumns, tableRowsToShow, showComparisonWithCapacityReport]
    );

    const { mutateAsync: handleCreateStrategicVolumeReport } = useMutation({
        mutationFn: createStrategicVolumeReport,
    });
    const { mutateAsync: handleCreateOneStrategicVolumeReport } = useMutation({
        mutationFn: createOneStrategicVolumeReport,
    });
    const { mutateAsync: handleUploadStrategicVolumes } = useMutation({
        mutationFn: uploadStrategicVolumes,
    });

    const { mutateAsync: handleMarkStrategicVolumeReportsRelease } = useMutation({
        mutationFn: markStrategicVolumeReportsRelease,
    });

    const goBackMonth = useCallback(
        (numOfMonths: number) => {
            const previousMonth = firstMonth.subtract(numOfMonths, 'months');
            setFirstMonth(previousMonth);
        },
        [firstMonth]
    );

    const goForwardMonth = useCallback(
        (numOfMonths: number) => {
            const nextMonth = firstMonth.add(numOfMonths, 'months');
            setFirstMonth(nextMonth);
        },
        [firstMonth]
    );

    const selectRow = useCallback((params: ICellRendererParams) => {
        setSelectedRows((prev) => {
            const rowIndex = prev.findIndex((row) => parseInt(row.strategicVolumeId) === parseInt(params.data.id));
            if (rowIndex === -1) {
                const { strategicVolumeId, strategicVolumeReports } = getStrategicVolumeAndReportsFromRow(params.data);
                return [...prev, { strategicVolumeId, strategicVolumeReports }];
            }
            const next = [...prev];
            next.splice(rowIndex, 1);
            return next;
        });
    }, []);

    const selectAllRows = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        const api = gridRef.current?.api;
        if (!api) return;

        const visibleIds = new Set<number>();
        const visibleRows: StrategicVolumeReleaseReportsForm[] = [];
        api.forEachNodeAfterFilter((rowNode) => {
            if (!rowNode.data) return;
            visibleIds.add(parseInt(rowNode.data.id));
            visibleRows.push(getStrategicVolumeAndReportsFromRow(rowNode.data));
        });

        setSelectedRows((prev) => {
            const hiddenSelected = prev.filter((r) => !visibleIds.has(parseInt(r.strategicVolumeId)));
            return checked ? [...hiddenSelected, ...visibleRows] : hiddenSelected;
        });
    }, []);

    useEffect(() => {
        selectedRowsRef.current = selectedRows;
        const api = gridRef.current?.api;
        if (api) {
            const columns = ['id', ...monthColumns.map((m) => m.format(DATE_FORMATS.YEAR_MONTH))];
            api.refreshCells({ columns, force: true });
        }
        freigabeHeaderForceUpdateRef.current();
    }, [selectedRows, monthColumns]);

    const FreigabeHeader = useMemo(
        () =>
            function FreigabeHeader() {
                const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
                useEffect(() => {
                    const update = () => forceUpdate();
                    freigabeHeaderForceUpdateRef.current = update;
                    const api = gridRef.current?.api;
                    if (!api) {
                        return () => {
                            freigabeHeaderForceUpdateRef.current = () => {};
                        };
                    }
                    api.addEventListener('filterChanged', update);
                    api.addEventListener('rowDataUpdated', update);
                    return () => {
                        api.removeEventListener('filterChanged', update);
                        api.removeEventListener('rowDataUpdated', update);
                        freigabeHeaderForceUpdateRef.current = () => {};
                    };
                }, []);

                const api = gridRef.current?.api;
                let visible = 0;
                let selectedVisible = 0;
                if (api) {
                    api.forEachNodeAfterFilter((node) => {
                        if (!node.data) return;
                        visible++;
                        if (isRowSelected(node.data, selectedRowsRef.current)) selectedVisible++;
                    });
                }
                return (
                    <div className="freigabe-cell" style={{ textAlign: 'center' }}>
                        <span>{t('field.release')}</span>
                        <br />
                        <Checkbox
                            onChange={selectAllRows}
                            checked={visible > 0 && selectedVisible === visible}
                            indeterminate={selectedVisible > 0 && selectedVisible < visible}
                            style={{ padding: 0 }}
                        ></Checkbox>
                    </div>
                );
            },
        [selectAllRows, t]
    );

    const createColumnDefs = useCallback((): ColDef[] => {
        let columns: ColDef[] = [
            {
                field: 'id',
                headerName: t('field.release'),
                cellClass: 'grid-cell-centered',
                suppressMovable: true,
                minWidth: 62,
                headerComponent: FreigabeHeader,
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <Checkbox
                            checked={isRowSelected(params.data, selectedRowsRef.current)}
                            disabled={!isTEFUser}
                            onChange={() => selectRow(params)}
                            style={{ padding: 0 }}
                        ></Checkbox>
                    );
                },
                editable: isTEFUser,
                hide: !isTEFUser && isOSPUser,
            },
            {
                field: 'forecasterNames',
                headerName: t('field.forecaster'),
                filter: 'agSetColumnFilter',
                minWidth: 120,
                flex: 2,
                valueFormatter: (params: ValueFormatterParams) => {
                    return Array.isArray(params.value) ? params.value.join(', ') : params.value;
                },
            },
            {
                field: 'workingUnitLocationId',
                headerName: t('field.workingUnitLocation'),
                minWidth: 130,
                flex: 2,
                hide: true,
            },
            {
                field: 'workingUnitId',
                headerName: t('field.workingUnit'),
                minWidth: 110,
                flex: 2,
                hide: true,
            },
            {
                field: 'worktype.name',
                headerName: t('field.worktype'),
                minWidth: 115,
                flex: 2,
            },
            {
                field: 'contact',
                headerName: t('field.contactType'),
                minWidth: 120,
            },
            {
                field: 'channel.name',
                headerName: t('field.channel'),
                minWidth: 90,
            },
            {
                field: 'task.name',
                headerName: t('field.task'),
                minWidth: 90,
            },
            {
                field: 'capacityType.name',
                headerName: t('field.capacity'),
                minWidth: 110,
            },
            {
                field: 'capacityType.unit',
                headerName: t('field.unit'),
                minWidth: 95,
            },
            {
                field: 'comment',
                headerName: t('field.comment'),
                // PATCH /strategic-volume is WFM-only; an editable cell for read
                // roles would accept input that silently fails to persist.
                editable: isTEFUser,
                suppressPaste: true,
                minWidth: 135,
            },
        ];

        for (const monthColumn of monthColumns) {
            columns.push({
                field: `${monthColumn.format(DATE_FORMATS.YEAR_MONTH)}`,
                headerName: `${monthColumn.locale(i18n.language).format(DATE_FORMATS.MONTH_SHORT_YEAR)}`,
                minWidth: 90,
                tooltipComponent: MatchingCapacityReportTooltip,
                tooltipField: `${monthColumn.format(DATE_FORMATS.YEAR_MONTH)}`,
                cellRenderer: (params: ICellRendererParams) => {
                    const { column, data } = params;
                    const key = column?.getColId() || column?.getId() || '';
                    let cellClasses = '';

                    if (isRowSelected(params.data, selectedRowsRef.current) && data[key].value) {
                        cellClasses += ' marked-for-release';
                    }

                    if (data?.[key]?.release === StrategicVolumeReportRelease.READY_FOR_RELEASE) {
                        cellClasses += ' ready-for-release';
                    } else if (data?.[key]?.release === StrategicVolumeReportRelease.RELEASED) {
                        cellClasses += ' released';
                    }

                    const difference = data[key]?.differenceWithCapacity;

                    return (
                        <span className={cellClasses}>
                            {params.valueFormatted}
                            {Number.isFinite(difference) && (
                                <span style={{ color: 'red' }}> ({formatNumber(difference)})</span>
                            )}
                        </span>
                    );
                },
                valueGetter: (params) => {
                    const value = params?.data[`${monthColumn.format(DATE_FORMATS.YEAR_MONTH)}`]?.value;
                    return value !== undefined && value !== null ? Math.round(value) : value;
                },
                valueSetter: (params) => {
                    const parsed = params.newValue === '' ? 0 : parseLocalizedNumber(params.newValue);
                    if (parsed === null && params.newValue !== '') {
                        // Invalid input - reject the change
                        return false;
                    }
                    params.data[`${monthColumn.format(DATE_FORMATS.YEAR_MONTH)}`].value = parsed;
                    return true;
                },
                valueFormatter: numberValueFormatter,
                suppressMovable: true,
                editable: isTEFUser,
            });
        }

        const defaultArrowColumnDefs = {
            field: ``,
            cellClass: 'grid-cell-centered',
            headerComponent: ButtonIconRenderer,
            maxWidth: 25,
            editable: false,
            sortable: false,
            resizable: false,
            filter: false,
            suppressMovable: true,
        };

        if (managerView) {
            columns = [
                {
                    ...defaultArrowColumnDefs,
                    headerComponentParams: {
                        icon: ArrowBackIosIcon,
                        onClick: () => goBackMonth(6),
                        iconStyle: { width: '14px', height: '14px' },
                    },
                    pinned: 'left',
                },
                ...columns,
                {
                    ...defaultArrowColumnDefs,
                    headerComponentParams: {
                        icon: ArrowForwardIosIcon,
                        onClick: () => goForwardMonth(6),
                        iconStyle: { width: '14px', height: '14px' },
                    },
                    pinned: 'right',
                },
            ];
        }

        return columns;
    }, [
        isTEFUser,
        managerView,
        isOSPUser,
        goBackMonth,
        goForwardMonth,
        monthColumns,
        selectRow,
        FreigabeHeader,
        t,
        i18n.language,
    ]);

    const columnDefs = useMemo(() => createColumnDefs(), [createColumnDefs]);

    const processCellForClipboard = (params: ProcessCellForExportParams) => {
        return params.value.toString().replace('.', ',');
    };

    const handleCellValueChange = (params: CellValueChangedEvent) => {
        const colId = params.column.getColId();
        if (isStringInYearMonthFormat(colId)) params.data[colId].changed = true;
    };

    const saveStrategicVolumeReport = useCallback(
        async ({
            tableRows,
            loggedInUserId,
            cellParams,
        }: {
            tableRows?: any;
            loggedInUserId?: number;
            cellParams?: CellEditingStoppedEvent;
        }) => {
            try {
                let isDataSaved = false;
                let numOfNewReports = 0;
                if (cellParams) {
                    if (cellParams.newValue) {
                        const data = {
                            report: {
                                ...cellParams.data,
                                date: cellParams.column.getColId(),
                                value: cellParams.newValue,
                            },
                            userId: loggedInUserId,
                        };

                        await handleCreateOneStrategicVolumeReport(data);
                        isDataSaved = true;
                    }
                } else {
                    const result = await handleCreateStrategicVolumeReport({ data: tableRows, userId: loggedInUserId });
                    isDataSaved = numOfNewReports > 0;
                    numOfNewReports = result.numOfNewReports;
                }

                if (isDataSaved) {
                    dispatchAlert({
                        message: t('notification.saved'),
                        type: 'success',
                    });
                } else if (numOfNewReports === 0) {
                    dispatchAlert({
                        message: t('notification.invalidData'),
                        type: 'error',
                    });
                }
            } catch (error) {
                showErrorNotification({
                    dispatchAlert,
                    message: t('notification.saveFailed'),
                    useSnackbar: false,
                });
            } finally {
                queryClient.invalidateQueries(strategicVolumeReportQueryKey);
            }
        },
        [
            dispatchAlert,
            handleCreateOneStrategicVolumeReport,
            handleCreateStrategicVolumeReport,
            strategicVolumeReportQueryKey,
            t,
        ]
    );

    const handleCellEdit = useCallback(
        async (params: CellEditingStoppedEvent) => {
            const colId = params.column.getColId();
            if (isStringInYearMonthFormat(colId)) {
                const enteredValue = parseStringToFloat(params.value);
                if (enteredValue && Number.isFinite(enteredValue)) {
                    saveStrategicVolumeReport({
                        cellParams: params,
                        loggedInUserId: userId,
                    });
                } else {
                    dispatchAlert({
                        message: t('notification.invalidNumber'),
                        type: 'error',
                    });
                }
            } else {
                await updateStrategicVolume({
                    id: params.data.strategicVolumeId,
                    [colId]: params.value,
                });
            }
        },
        [dispatchAlert, saveStrategicVolumeReport, userId, t]
    );

    const onPasteEnd = useCallback(
        () =>
            saveStrategicVolumeReport({
                tableRows,
                loggedInUserId: userId,
            }),
        [saveStrategicVolumeReport, tableRows, userId]
    );

    const markReportRelease = useCallback(
        (rows: StrategicVolumeReleaseReportsForm[], releaseType: number) => {
            handleMarkStrategicVolumeReportsRelease({ strategicVolumes: rows, releaseType }).then((res) => {
                if (res.success) {
                    if (showConfirmationDialog) setShowConfirmationDialog(false);
                    if (releaseType === StrategicVolumeReportRelease.RELEASED) setSelectedRows([]);

                    queryClient.invalidateQueries(strategicVolumeReportQueryKey);
                }
            });
        },
        [handleMarkStrategicVolumeReportsRelease, showConfirmationDialog, strategicVolumeReportQueryKey]
    );

    const handleMarkReportReleaseClick = useCallback(
        async (releaseType: number) => {
            if (releaseType === StrategicVolumeReportRelease.RELEASED) {
                setShowConfirmationDialog(true);
                return;
            }

            if (!selectedRows.length) {
                showErrorNotification({
                    dispatchAlert,
                    message: t('notification.noRowsSelected'),
                    useSnackbar: false,
                });

                return;
            }

            markReportRelease(selectedRows, releaseType);
        },
        [dispatchAlert, selectedRows, markReportRelease, t]
    );

    const handleDeactivate = useCallback(() => {
        const rowsToDeactivate = selectedRows.reduce((acc: StrategicVolumeReleaseReportsForm[], row) => {
            const filteredReports = row.strategicVolumeReports.filter(
                (report) => report.release === StrategicVolumeReportRelease.RELEASED
            );

            if (filteredReports.length > 0) {
                acc.push({
                    strategicVolumeId: row.strategicVolumeId,
                    strategicVolumeReports: filteredReports,
                });
            }

            return acc;
        }, []);

        markReportRelease(rowsToDeactivate, StrategicVolumeReportRelease.READY_FOR_RELEASE);
    }, [selectedRows, markReportRelease]);

    const closeConfirmationDialog = useCallback(() => {
        setShowConfirmationDialog(false);
    }, []);

    const handleSetPercentage = useCallback((value: number) => {
        setPercentage(value);
    }, []);

    const uploadFiles = useCallback(
        (files: File[], closeDropzoneDialog: any) => {
            setLoading(true);

            const file = files[0];

            const reader = new FileReader();
            reader.onload = async (e) => {
                const data = parseXLSXData(e, 'locationCode'); // add some validation here or in the backend

                await handleUploadStrategicVolumes({ data, userId: userId, setPercentage: handleSetPercentage })
                    .then(async (res) => {
                        const resData = parseUploadResponse(res, 'Strategic Volume');
                        const { success, errorMessage, uploadContainsInactiveVolumes, results } = resData;

                        if (uploadContainsInactiveVolumes) {
                            dispatchAlert({
                                message: t('upload.inactiveVolumesWarning'),
                                type: 'success',
                                useSnackbar: false,
                            });

                            return;
                        }

                        if (results) {
                            setUploadResults(results);
                            setShowUploadResults(true);
                        } else if (success) {
                            showSuccessfulUploadNotification(dispatchAlert);
                        } else {
                            showErrorUploadNotification({
                                dispatchAlert,
                                errorMessage,
                                customErrorMessage: undefined,
                            });
                        }
                    })
                    .catch(async (error) => {
                        Sentry.captureException(error);
                        showErrorUploadNotification({
                            dispatchAlert,
                            errorMessage: error.message,
                            customErrorMessage: undefined,
                        });
                    })
                    .finally(async () => {
                        setCommonStateAfterDataUpload(setLoading, setPercentage, closeDropzoneDialog);

                        queryClient.invalidateQueries([
                            QueryKey.LastUploadDate + '-strategicVolumeReport',
                            { location: selectedLocation },
                        ]);
                    });
            };
            reader.readAsArrayBuffer(file);
        },
        [handleUploadStrategicVolumes, userId, handleSetPercentage, dispatchAlert, selectedLocation, t]
    );

    const applyFilterOnForecaster = useCallback(() => {
        let values: string[] = [];
        const name = loggedInUser?.name || '';
        const filterModel = gridRef?.current?.api.getColumnFilterModel<{ values: string[] }>('forecasterNames');
        if (filterModel?.values?.includes(name)) {
            values = [name];
        }
        if (values.length === 0) return; // no need to apply filter if the user is not a forecaster
        if (gridRef.current?.api) {
            gridRef.current.api.setColumnFilterModel('forecasterNames', {
                filterType: 'set',
                values,
            });
            gridRef.current.api.onFilterChanged();
        }
        isForecasterFilterApplied.current = true;
    }, [loggedInUser?.name]);

    const onFirstDataRendered = (params: FirstDataRenderedEvent) => {
        params.api.sizeColumnsToFit();
    };

    useEffect(() => {
        if (isTEFUser && isMounted && tableRows.length > 0 && gridRef.current?.api) {
            if (!isForecasterFilterApplied.current) {
                applyFilterOnForecaster();
            }
        }
    }, [applyFilterOnForecaster, tableRows.length, isMounted, gridRef.current?.api, isTEFUser]);

    return (
        <Container wide={false}>
            <GridContainer sx={loading ? classes.containerOpacity : {}}>
                {loggedInUser && (
                    <UserRoleLocation
                        selectedLocation={selectedLocation}
                        locationOptions={locationOptions}
                        onChange={(value: string) => {
                            setSelectedLocation(value);
                            setShowComparisonWithCapacityReport(false);
                        }}
                    ></UserRoleLocation>
                )}
                <Grid item xs={isTEFUser ? 7 : 10} md={3} lg={2}>
                    <LastUpload showInfoIcon={false} date={lastUploadDate} onClick={undefined} />
                </Grid>
                <Grid item xs={1}>
                    <StrategicVolumeExportButton locations={locationOptions}></StrategicVolumeExportButton>
                </Grid>
                {isTEFUser && (
                    <Grid
                        xs={12}
                        item
                        sx={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}
                    >
                        <UploadXlsxFileButton
                            buttonName={t('action.upload')}
                            variant="contained"
                            uploadFiles={uploadFiles}
                            dialogTitle={t('upload.dialogTitle')}
                            style={{ marginRight: '0.5rem' }}
                        ></UploadXlsxFileButton>
                        <Button
                            onClick={() => setShowComparisonWithCapacityReport(true)}
                            variant="outlined"
                            style={{ marginRight: '0.5rem' }}
                        >
                            {t('action.compare')}
                        </Button>
                        <Button
                            onClick={() => handleMarkReportReleaseClick(StrategicVolumeReportRelease.READY_FOR_RELEASE)}
                            variant="contained"
                            color="orange"
                        >
                            {t('action.markForRelease')}
                        </Button>
                        <Button
                            onClick={() => handleMarkReportReleaseClick(StrategicVolumeReportRelease.RELEASED)}
                            variant="contained"
                            style={{ backgroundColor: 'var(--lightGreen)', color: 'white', marginRight: '0.5rem' }}
                        >
                            {t('action.activateRelease')}
                        </Button>
                        {selectedRows.length > 0 && (
                            <Button
                                onClick={handleDeactivate}
                                variant="contained"
                                style={{ backgroundColor: 'grey', color: 'white' }}
                            >
                                {t('action.deactivateSelection')}
                            </Button>
                        )}
                    </Grid>
                )}

                <TableGridItem
                    ref={gridRef}
                    rowData={tableRows}
                    defaultColDef={{
                        editable: false,
                        filter: true,
                        sortable: true,
                    }}
                    columnDefs={columnDefs}
                    onCellEditingStopped={handleCellEdit}
                    onCellValueChanged={handleCellValueChange}
                    onPasteEnd={onPasteEnd}
                    processCellForClipboard={processCellForClipboard}
                    tooltipShowDelay={0}
                    tooltipHideDelay={3000}
                    onFirstDataRendered={onFirstDataRendered}
                ></TableGridItem>
            </GridContainer>
            {showConfirmationDialog && (
                <ConfirmationDialog
                    open={showConfirmationDialog}
                    title={t('release.confirmTitle')}
                    text={t('release.confirmText')}
                    onClose={closeConfirmationDialog}
                    onConfirm={() => markReportRelease(selectedRows, StrategicVolumeReportRelease.RELEASED)}
                />
            )}

            <StrategicVolumeUploadResultsDialog
                open={showUploadResults}
                results={uploadResults}
                onClose={() => setShowUploadResults(false)}
            />

            <PercentageLoadingOverlay loading={loading} percentage={percentage} disableShrink={true} />
        </Container>
    );
};

export default StrategicVolume;
